/**
 * Search form state yönetimi hook'u
 * Form değerleri, autocomplete seçimleri ve form güncellemelerini yönetir
 */

import { useState, useCallback } from 'react'
import type { Suggestion } from '@/types/product-search'

export interface UseSearchFormStateReturn {
    formValues: Record<string, any>
    selectedSuggestionTypes: Record<string, 'category' | 'product' | 'station_city'>
    selectedSuggestionSlugs: Record<string, string>
    /** Autocomplete seçiminde API'den gelen product_type (URL path için) */
    selectedSuggestionProductTypes: Record<string, string>
    updateFormValue: (fieldKey: string, value: any) => void
    clearAutocompleteSelection: (fieldKey: string) => void
    saveAutocompleteSelection: (fieldKey: string, suggestion: Suggestion) => void
    handleAutocompleteSelect: (fieldKey: string) => (suggestion: Suggestion | null) => void
    setCategoryToForm: (fieldKey: string, categorySlug: string, categoryName?: string) => void
}

export function useSearchFormState(): UseSearchFormStateReturn {
    const [formValues, setFormValues] = useState<Record<string, any>>({})
    const [selectedSuggestionTypes, setSelectedSuggestionTypes] = useState<Record<string, 'category' | 'product' | 'station_city'>>({})
    const [selectedSuggestionSlugs, setSelectedSuggestionSlugs] = useState<Record<string, string>>({})
    const [selectedSuggestionProductTypes, setSelectedSuggestionProductTypes] = useState<Record<string, string>>({})
    
    const updateFormValue = useCallback((fieldKey: string, value: any) => {
        setFormValues(prev => ({ ...prev, [fieldKey]: value }))
    }, [])
    
    const clearAutocompleteSelection = useCallback((fieldKey: string) => {
        updateFormValue(fieldKey, '')
        setSelectedSuggestionTypes(prev => {
            const newTypes = { ...prev }
            delete newTypes[fieldKey]
            return newTypes
        })
        setSelectedSuggestionSlugs(prev => {
            const newSlugs = { ...prev }
            delete newSlugs[fieldKey]
            return newSlugs
        })
        setSelectedSuggestionProductTypes(prev => {
            const next = { ...prev }
            delete next[fieldKey]
            return next
        })
    }, [updateFormValue])
    
    const saveAutocompleteSelection = useCallback((fieldKey: string, suggestion: Suggestion) => {
        updateFormValue(fieldKey, suggestion.name)
        setSelectedSuggestionSlugs(prev => ({ ...prev, [fieldKey]: suggestion.slug || suggestion.name }))
        setSelectedSuggestionTypes(prev => ({ ...prev, [fieldKey]: suggestion.type }))
        if (suggestion.product_type) {
            setSelectedSuggestionProductTypes(prev => ({ ...prev, [fieldKey]: suggestion.product_type as string }))
        } else {
            setSelectedSuggestionProductTypes(prev => {
                const next = { ...prev }
                delete next[fieldKey]
                return next
            })
        }
    }, [updateFormValue])
    
    const handleAutocompleteSelect = useCallback((fieldKey: string) => (suggestion: Suggestion | null) => {
        if (!suggestion) {
            clearAutocompleteSelection(fieldKey)
            return
        }
        saveAutocompleteSelection(fieldKey, suggestion)
    }, [clearAutocompleteSelection, saveAutocompleteSelection])
    
    const setCategoryToForm = useCallback((fieldKey: string, categorySlug: string, categoryName?: string) => {
        const fieldKeys = fieldKey.split(',').map(k => k.trim()).filter(Boolean)
        const keys = fieldKeys.length > 0 ? fieldKeys : [fieldKey]
        keys.forEach(key => {
            setFormValues(prev => ({ ...prev, [key]: categoryName || categorySlug }))
            setSelectedSuggestionSlugs(prev => ({ ...prev, [key]: categorySlug }))
            setSelectedSuggestionTypes(prev => ({ ...prev, [key]: 'category' }))
        })
    }, [])
    
    return {
        formValues,
        selectedSuggestionTypes,
        selectedSuggestionSlugs,
        selectedSuggestionProductTypes,
        updateFormValue,
        clearAutocompleteSelection,
        saveAutocompleteSelection,
        handleAutocompleteSelect,
        setCategoryToForm,
    }
}